import { useState } from "react";
import { childAuth, type ChildSessionResponse } from "../api";

interface TypedLoginFormProps {
  childName: string;
  familyAccountId: string;
  onSuccess: (session: ChildSessionResponse) => void;
  onCancel: () => void;
  maxAttempts?: number;
  /** If provided, pre-fills the passphrase and auto-submits */
  initialPassphrase?: string;
  /** Show "remember me" checkbox */
  showRememberMe?: boolean;
  onRememberMe?: (passphrase: string) => void;
}

/**
 * Typed login form for child authentication.
 * Large, child-friendly inputs with visual feedback.
 * Uses password input with visibility toggle for password manager support.
 */
export function TypedLoginForm({
  childName,
  familyAccountId,
  onSuccess,
  onCancel,
  maxAttempts = 3,
  initialPassphrase,
  showRememberMe = true,
  onRememberMe,
}: TypedLoginFormProps) {
  const [passphrase, setPassphrase] = useState(initialPassphrase ?? "");
  const [showPassphrase, setShowPassphrase] = useState(true);
  const [rememberMe, setRememberMe] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLocked, setIsLocked] = useState(false);
  const [countdown, setCountdown] = useState(0);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!passphrase.trim() || submitting || isLocked) return;

    setSubmitting(true);
    setErrorMessage("");

    try {
      const result = await childAuth.typedLogin(childName, passphrase.trim(), familyAccountId);
      if (rememberMe && onRememberMe) onRememberMe(passphrase.trim());
      onSuccess(result);
    } catch (err) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      if (err instanceof Error && err.message.includes("locked")) {
        triggerLockout();
      } else if (newAttempts >= maxAttempts) {
        triggerLockout();
      } else {
        setErrorMessage(getEncouragingMessage(newAttempts));
      }
    } finally {
      setSubmitting(false);
      setPassphrase("");
    }
  }

  function triggerLockout() {
    setIsLocked(true);
    setErrorMessage("Too many tries. Ask a parent for help!");
    let remaining = 300;
    setCountdown(remaining);
    const interval = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        setIsLocked(false);
        setAttempts(0);
        setErrorMessage("");
      }
    }, 1000);
  }

  function getEncouragingMessage(attempt: number): string {
    const messages = [
      "Hmm, that didn't match. Check your spelling!",
      "Almost! Try one more time.",
      "That's okay! Give it another go.",
    ];
    return messages[Math.min(attempt - 1, messages.length - 1)];
  }

  if (isLocked) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.lockIcon}>🔒</div>
          <h2 style={styles.title}>Login Locked</h2>
          <p style={styles.message}>{errorMessage}</p>
          <p style={styles.countdown}>
            Try again in {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, "0")}
          </p>
          <button onClick={onCancel} style={styles.secondaryButton}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>Hi {childName}! ✏️</h2>
        <p style={styles.message}>Type your secret phrase below</p>

        <form onSubmit={handleSubmit} autoComplete="on">
          <div style={{ position: "relative" }}>
            <input
              type={showPassphrase ? "text" : "password"}
              name="password"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              placeholder="Your secret phrase..."
              style={{ ...styles.input, paddingRight: 48 }}
              autoFocus
              disabled={submitting}
              autoComplete="current-password"
              aria-label="Secret phrase"
            />
            <button
              type="button"
              onClick={() => setShowPassphrase(!showPassphrase)}
              style={styles.visibilityToggle}
              aria-label={showPassphrase ? "Hide passphrase" : "Show passphrase"}
              tabIndex={-1}
            >
              {showPassphrase ? "🙈" : "👁️"}
            </button>
          </div>

          {/* Hidden username field for password manager association */}
          <input type="hidden" name="username" value={childName} autoComplete="username" />

          {showRememberMe && (
            <label style={styles.rememberRow}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ width: 20, height: 20 }}
              />
              <span style={{ fontSize: 15, color: "#555" }}>Remember me on this device</span>
            </label>
          )}

          {errorMessage && (
            <p style={styles.errorText}>{errorMessage}</p>
          )}

          {attempts > 0 && (
            <p style={styles.attemptCounter}>
              Attempt {attempts} of {maxAttempts}
            </p>
          )}

          <button
            type="submit"
            disabled={!passphrase.trim() || submitting}
            style={{
              ...styles.primaryButton,
              opacity: !passphrase.trim() || submitting ? 0.6 : 1,
            }}
          >
            {submitting ? "Checking..." : "Let me in! 🚀"}
          </button>
        </form>

        <div style={styles.linkRow}>
          <button onClick={onCancel} style={styles.linkButton}>
            Go back
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: 16,
  },
  card: {
    background: "#fff",
    padding: 32,
    borderRadius: 24,
    textAlign: "center",
    width: "90%",
    maxWidth: 400,
    boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#333",
    marginBottom: 8,
  },
  message: {
    fontSize: 18,
    color: "#555",
    marginBottom: 24,
  },
  input: {
    width: "100%",
    padding: 16,
    borderRadius: 12,
    border: "2px solid #ddd",
    fontSize: 20,
    textAlign: "center",
    marginBottom: 16,
    boxSizing: "border-box",
    outline: "none",
  },
  errorText: {
    color: "#e53935",
    fontSize: 16,
    marginBottom: 12,
  },
  attemptCounter: {
    fontSize: 14,
    color: "#999",
    marginBottom: 12,
  },
  primaryButton: {
    width: "100%",
    padding: "16px 32px",
    borderRadius: 12,
    border: "none",
    background: "#4CAF50",
    color: "#fff",
    fontSize: 20,
    fontWeight: "bold",
    cursor: "pointer",
    marginBottom: 16,
  },
  secondaryButton: {
    padding: "12px 24px",
    borderRadius: 8,
    border: "1px solid #ddd",
    background: "#fff",
    color: "#333",
    fontSize: 16,
    cursor: "pointer",
  },
  lockIcon: {
    fontSize: 64,
    marginBottom: 16,
  },
  countdown: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#e53935",
    marginBottom: 16,
  },
  linkRow: {
    display: "flex",
    justifyContent: "center",
    gap: 16,
  },
  linkButton: {
    background: "none",
    border: "none",
    color: "#667eea",
    fontSize: 14,
    cursor: "pointer",
    textDecoration: "underline",
  },
  visibilityToggle: {
    position: "absolute" as const,
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    fontSize: 20,
    cursor: "pointer",
    padding: 4,
  },
  rememberRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
    cursor: "pointer",
  },
};
